import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import {
  GetEntityRelationLifecycleDocument,
  type GetEntityRelationLifecycleQuery,
  type GetEntityRelationLifecycleQueryVariables,
} from "~/features/entities/gqls";
import type { EntityRelationLifecycle } from "~/features/entities/types";
import { toEntityRelationLifecycle } from "~/features/entities/utils/transformers";
import { useTenant } from "~/features/tenant/context";

const EMPTY: EntityRelationLifecycle = { toDeactivate: [], activatable: [] };

/**
 * What a lifecycle change would do to the entity's relations.
 *
 * Fetched only while the confirm dialog is open: it is a preview for the operator, and the
 * authoritative set is recomputed on the server inside the transaction that performs the write.
 * A deactivation therefore acts on whatever is attached at approval time, not on this list.
 */
export function useEntityRelationLifecycle(id: string, enabled: boolean) {
  const { tenantId } = useTenant();
  const locale = useDisplayLocale();
  const { data, loading, error } = useQuery<GetEntityRelationLifecycleQuery, GetEntityRelationLifecycleQueryVariables>(
    GetEntityRelationLifecycleDocument,
    { variables: { tenantId: tenantId ?? "", id }, skip: !enabled || !tenantId, fetchPolicy: "cache-and-network" },
  );

  const relations = useMemo(() => (data ? toEntityRelationLifecycle(data.records[0], locale) : EMPTY), [data, locale]);

  // `loading` stays true through a cache-and-network refetch even with a cached answer in hand,
  // and that is what the caller wants: confirming against a stale list would activate the wrong
  // relations, or send an id the approval will refuse. An error reports as an error rather than
  // as an entity with no relations, which is what an empty list would otherwise look like.
  return { relations, loading, error };
}
